import * as angular from 'angular';
import * as _ from 'lodash';

import { ICacheService, ILoggerService } from './general';

export interface IRequestService {
    cleanAllCache(): void;
    cleanCache(url: string): void;
    cleanCacheAtV1(url: string, params?: any): void;
    cleanCachedPosts(constrolerAction: string);
    cleanCachedPostValue(controllerAction: string, configuration: any, params?: { [key: string]: any });
    getUrlV1(): string;
    getApiUrl(value?: string): string;
    delete(url: string, data?: any, params?: any): angular.IPromise<any>;
    deleteAtV1(controllerAction: string, data?: any, params?: any): angular.IPromise<any>;
    get(url: string, params?: any, preventCancel?: boolean, cancel?: angular.IPromise<any>): angular.IPromise<any>;
    getAtV1<T>(controllerAction: string, params?: any, preventCancel?: boolean, cancel?: angular.IPromise<any>): angular.IPromise<T>;
    post(url: string, data?: any, params?: any, preventCancel?: boolean, cancel?: angular.IPromise<any>, responseType?: string): angular.IPromise<any>;
    postAtV1<T>(controllerAction: string, data?: any, params?: any, preventCancel?: boolean, cancel?: angular.IPromise<any>, responseType?: string, headers?: any): angular.IPromise<T>;
    cachedPostAtV1<T>(controllerAction: string, data: any, params?: { [key: string]: any }, preventCancel?: boolean, cancel?: angular.IPromise<any>, force?: boolean): angular.IPromise<T>;
    postAtV1ForFile<T>(controllerAction: string, data?: any, params?: any, cancel?: angular.IPromise<any>): angular.IPromise<T>;
    cancellers: angular.IDeferred<void>[];
    cancelPendingRequest(): void;
}

export class RequestService implements IRequestService {
    static serviceId = 'requestService';

    private static baseUrl: string;

    cancellers: angular.IDeferred<void>[];

    static $inject = [
        '$http',
        '$q',
        'cacheService',
        'loggerService'
    ];

    constructor(
        private _$http: angular.IHttpService,
        private _$q: angular.IQService,
        private _cache: ICacheService,
        private _loggerService: ILoggerService
    ) {
        this.cancellers = [];
    }

    static setBaseUrl(url: string) {
        RequestService.baseUrl = url;
    }

    cleanAllCache(): void {
        this._cache.removeAll();
    }

    cleanCache(url: string): void {
        this._cache.remove(url);
        // allow to clean also with or without / ending
        const url2 = (url[url.length - 1] === '/')
            ? url.substr(0, url.length - 1)
            : url + '/';
        this._cache.remove(url2);
        this._loggerService.log(`Cache cleaned at address ${url}`, false);
    }

    cleanCacheAtV1(url: string, params?: any): void {
        if (params) {
            let tmp = [];
            for (const key of Object.keys(params)) {
                tmp.push(`${key}=${params[key]}`);
            }
            tmp = _.sortBy(tmp, t => t);
            url = `${url}?${tmp.join('&')}`;
        }
        this.cleanCache(this.getUrlV1() + url);
    }

    cleanCachedPosts(controllerAction: string) {
        this._cache.removeStartingWith(controllerAction);
    }

    cleanCachedPostValue(controllerAction: string, configuration: any, params?: { [key: string]: any }) {
        let key = `${controllerAction}${JSON.stringify(configuration)}`;
        if (params) {
            key = `${key}${JSON.stringify(params)}`;
        }
        this._cache.remove(key);
    }

    get(url, params?: any, preventCancel?: boolean, cancel?: angular.IPromise<any>): angular.IPromise<any> {
        const request = this.createRequest(url, 'GET', null, params, preventCancel, cancel);
        return this.executeRequest(request);
    }

    getAtV1(controllerAction, params?, preventCancel?: boolean, cancel?: angular.IPromise<any>): angular.IPromise<any> {
        const url = this.buildV1Url(controllerAction);
        return this.get(url, params, preventCancel, cancel);
    }

    post(url, data?: any, params?: any, preventCancel?: boolean, cancel?: angular.IPromise<any>, responseType?: string, headers?: any): angular.IPromise<any> {
        const request = this.createRequest(url, 'POST', data, params, preventCancel, cancel, responseType, headers);
        return this.executeRequest(request);
    }

    postAtV1<T>(controllerAction: string, data?: any, params?: any, preventCancel?: boolean, cancel?: angular.IPromise<any>, responseType?: string, headers?: any): angular.IPromise<T> {
        const url = this.buildV1Url(controllerAction);
        return this.post(url, data, params, preventCancel, cancel, responseType, headers);
    }

    // Allow retrieval of a blob to download a file (responseType = 'blob' in request)
    postAtV1ForFile<T>(controllerAction: string, data?: any, params?: any, cancel?: angular.IPromise<any>): angular.IPromise<T> {
        const url = this.buildV1Url(controllerAction);
        return this.post(url, data, params, true, cancel, 'blob');
    }

    delete(url, data?: any, params?: any): angular.IPromise<any> {
        const request = this.createRequest(url, 'DELETE', data, params, true);
        return this.executeRequest(request);
    }

    deleteAtV1(controllerAction: string, data?, params?): angular.IPromise<any> {
        const url = this.buildV1Url(controllerAction);
        return this.delete(url, data, params);
    }

    cachedPostAtV1<T>(
        controllerAction: string,
        data: any,
        params?: { [key: string]: any },
        preventCancel?: boolean,
        cancel?: angular.IPromise<any>,
        force?: boolean
    ): angular.IPromise<T> {
        const defered = this._$q.defer<T>();
        let key = `${controllerAction}${JSON.stringify(data)}`;
        if (params) {
            key = `${key}${JSON.stringify(params)}`;
        }
        if (force) {
            this.cleanCacheAtV1(controllerAction);
            this.cleanCachedPostValue(controllerAction, data);
        }
        const cachedValue = this.getCacheValue<T>(key);
        if (cachedValue) {
            defered.resolve(cachedValue);
        } else {
            this.postAtV1<T>(controllerAction, data, params, preventCancel, cancel)
                .then(results => {
                    this.setCacheValue(key, results);
                    defered.resolve(results);
                })
                .catch((reason) => {
                    defered.reject(reason);
                });
        }
        return defered.promise;
    }

    getApiUrl(value?: string): string {
        if (arguments.length) {
            RequestService.baseUrl = value;
        }
        return RequestService.baseUrl ? RequestService.baseUrl : 'https://localhost:44300/';
    }

    getUrlV1(): string {
        return this.getApiUrl() + 'api/v1/';
    }

    // Cancel all ongoind request and create a new default canceller
    cancelPendingRequest() {
        this.cancellers.forEach(c => c.resolve());
        this.cancellers = [];
    }

    private getCacheValue<T>(key: string): T {
        return this._cache.get(key) ?? null;
    }

    private setCacheValue<T>(key: string, value: T) {
        this._cache.get(key);
        const existing = this._cache.get(key);
        if (existing) {
            this._cache.remove(key);
        }
        this._cache.put(key, value);
    }

    private createRequest(url: string,
        verb: string,
        data?: any,
        params?: any,
        preventCancel: boolean = false,
        cancel?: angular.IPromise<any>,
        responseType = '',
        headers: any = undefined): angular.IRequestConfig {
        const request: angular.IRequestConfig = {
            url: url,
            method: verb,
            cache: true,
            headers: headers
        };
        if (responseType) {
            request.responseType = responseType;
        }

        if (data) {
            request.data = data;
        }
        if (params) {
            request.params = params;
        }
        if (cancel) {
            request.timeout = cancel;
        } else {
            if (!preventCancel) {
                const canceler = this._$q.defer<void>();
                this.cancellers.push(canceler);
                request.timeout = canceler.promise;
            }
        }

        return request;
    }

    private executeRequest(request: angular.IRequestConfig): angular.IPromise<any> {
        const defer = this._$q.defer();

        this._$http(request)
            .then(data => {
                defer.resolve(data.data);
            })
            .catch((reason?) => {
                try {
                    if (reason && reason.status === -1) { // request ABORTED
                        this._loggerService.logWarning(`Request was aborted ${request.url}`, false);
                    } else if (reason && reason.data && (reason.data.message || reason.data.Message)) {
                        const msg = reason.data.message ? reason.data.message : reason.data.Message;
                        this._loggerService.logError(msg);
                    } else {
                        this._loggerService.logError(`Error while requesting to address ${request.url}`, reason?.status !== 401);
                    }
                } catch (e) {
                    this._loggerService.logError(`Error while requesting to address ${request.url}`);
                }
                defer.reject(reason);
            })
            .finally(() => {
                this.clearCanceler(request.timeout);
            });

        return defer.promise;
    }

    private clearCanceler(timeout: any) {
        if (timeout) {
            this.cancellers = this.cancellers.filter(c => c.promise !== timeout);
        }
    }

    private buildV1Url(controllerAction: string): string {
        const url = this.getUrlV1() + controllerAction;
        return url;
    }
}